/**
 * Admin Notifications Service
 * API client for admin notification dashboard endpoints
 */

function normalizeSuccessPayload(payload) {
  if (
    payload &&
    payload.success === true &&
    Object.prototype.hasOwnProperty.call(payload, 'data')
  ) {
    const normalized =
      payload.data &&
      typeof payload.data === 'object' &&
      !Array.isArray(payload.data)
        ? { ...payload.data }
        : { data: payload.data }

    return {
      success: true,
      ...normalized,
      ...(payload.meta !== undefined ? { meta: payload.meta } : {}),
    }
  }

  return payload
}

function getErrorMessage(payload, fallbackMessage) {
  return (
    payload?.error?.message ||
    payload?.error ||
    payload?.message ||
    fallbackMessage
  )
}

/**
 * Get notification statistics
 * @param {string} startDate - ISO 8601 date string (optional)
 * @param {string} endDate - ISO 8601 date string (optional)
 * @returns {Promise<Object>} Stats object
 */
export async function getNotificationStats(startDate, endDate) {
  const params = new URLSearchParams()
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)

  const url = `/api/1.0/admin/notification-stats${params.toString() ? `?${params.toString()}` : ''}`

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(
      getErrorMessage(error, 'Failed to fetch notification stats'),
    )
  }

  return normalizeSuccessPayload(await response.json())
}

/**
 * Get notification configuration
 * @returns {Promise<Object>} Configuration object
 */
export async function getNotificationConfig() {
  const response = await fetch('/api/1.0/admin/notification-config', {
    method: 'GET',
    credentials: 'include',
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(
      getErrorMessage(error, 'Failed to fetch notification config'),
    )
  }

  return normalizeSuccessPayload(await response.json())
}

/**
 * Update notification configuration
 * @param {Object} updates - Configuration updates object
 * @returns {Promise<Object>} Updated configuration
 */
export async function updateNotificationConfig(updates) {
  const response = await fetch('/api/1.0/admin/notification-config', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(updates),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(
      getErrorMessage(error, 'Failed to update notification config'),
    )
  }

  return normalizeSuccessPayload(await response.json())
}

/**
 * Get email notification logs (auto-triggered emails)
 * @param {Object} options - Filter options
 * @returns {Promise<Object>} Email activity result
 */
export async function getNotificationEmailLogs(options = {}) {
  const params = new URLSearchParams()

  Object.entries(options).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      params.append(key, value)
    }
  })

  const url = `/api/1.0/admin/notification-email-logs${params.toString() ? `?${params.toString()}` : ''}`

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(
      getErrorMessage(error, 'Failed to fetch notification email logs'),
    )
  }

  return normalizeSuccessPayload(await response.json())
}

/**
 * Get notification email template tester metadata
 * @returns {Promise<Object>} Template metadata
 */
export async function getNotificationEmailTesterTemplates() {
  const response = await fetch('/api/1.0/admin/notification-email-tester', {
    method: 'GET',
    credentials: 'include',
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(
      getErrorMessage(error, 'Failed to fetch email tester templates'),
    )
  }

  return normalizeSuccessPayload(await response.json())
}

/**
 * Render and optionally send a notification email template
 * @param {Object} options
 * @param {string} options.template_key
 * @param {Object} options.payload
 * @param {boolean} [options.preview_only=true]
 * @param {string} [options.to]
 * @returns {Promise<Object>} Render/send result
 */
export async function testNotificationEmailTemplate(options) {
  const response = await fetch('/api/1.0/admin/notification-email-tester', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(options),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(getErrorMessage(error, 'Failed to test email template'))
  }

  return normalizeSuccessPayload(await response.json())
}
